import { mockUsers } from "../constants/mockData";
import { publicEndpoints } from "../constants/endpoints";
import { endpoints } from "../constants/endpoints";
import { api } from "./api";
import { getApiOrganizationId } from "../utils/orgIdUtils";

import {
  ConfirmationResponseDTO,
  ForgotPasswordRequestDTO,
  ForgotPasswordResponseDTO,
  LoginRequestDTO,
  LoginResponseDTO,
  PasswordUpdateRequestDTO,
  PasswordUpdateResponseDTO,
} from "../dto/types";

export type LoginData = {
  emailId: string;
  password: string;
};

export const loginWithBackend = async (data: LoginData) => {
  try {
    const organizationId = getApiOrganizationId();
    const response = await api.post(`/${publicEndpoints.signin}`, data, {
      headers: { orgId: organizationId },
    });
    return response.data;
  } catch (error: any) {
    if (error?.response?.data) {
      console.error("Login error:", error.response.data);
    } else {
      console.error("Login error:", error.message || "Unknown error");
    }
    throw error;
  }
};

export const autoLoginWithToken = async (token: string) => {
  try {
    const response = await api.post("/auto-login", { token });
    return response.data;
  } catch (error: any) {
    if (error?.response?.data) {
      console.error("Auto-login error:", error.response.data);
    } else {
      console.error("Auto-login error:", error.message || "Unknown error");
    }
    throw error;
  }
};

export const logoutWithBackend = async () => {
  const emailId = localStorage.getItem("userEmail");
  const refreshToken = sessionStorage.getItem("refreshToken");
  if (!emailId || !refreshToken) {
    throw new Error("Missing email or refresh token for logout.");
  }
  try {
    const response = await api.post("/logout", { emailId, refreshToken });
    localStorage.removeItem("accessToken");
    localStorage.removeItem("idToken");
    localStorage.removeItem("refreshToken");
    sessionStorage.removeItem("refreshToken");
    console.log("Logout successfullllll", response.data);

    return response.data;
  } catch (error: any) {
    if (error?.response?.data) {
      console.error("Logout error:", error.response.data);
    } else {
      console.error("Logout error:", error.message || "Unknown error");
    }
    throw error;
  }
};

export const refreshTokenMiddleware = async () => {
  const emailId = localStorage.getItem("userEmail");
  const refreshToken = sessionStorage.getItem("refreshToken");
  if (!emailId || !refreshToken) {
    throw new Error("Missing email or refresh token for refresh.");
  }
  try {
    const response = await api.post("/refresh-token", {
      emailId,
      refreshToken,
    });
    const tokens = response.data?.data?.tokens;
    if (tokens?.accessToken) {
      localStorage.setItem("accessToken", tokens.accessToken);
    }
    if (tokens?.idToken) {
      localStorage.setItem("idToken", tokens.idToken);
    }
    if (tokens?.refreshToken) {
      localStorage.setItem("refreshToken", tokens.refreshToken);
      sessionStorage.setItem("refreshToken", tokens.refreshToken);
    }
    return tokens?.accessToken;
  } catch (error: any) {
    if (error?.response?.data) {
      console.error("Refresh token error:", error.response.data);
    } else {
      console.error("Refresh token error:", error.message || "Unknown error");
    }
    throw error;
  }
};

export const forgotPasswordWithBackend = async (emailId: string) => {
  try {
    const response = await api.post("/forgot-password", { emailId });
    return response.data;
  } catch (error: any) {
    if (error?.response?.data) {
      console.error("Forgot Password error:", error.response.data);
    } else {
      console.error("Forgot Password error:", error.message || "Unknown error");
    }
    throw error;
  }
};

export const resetPasswordWithBackend = async (
  emailId: string,
  code: string,
  password: string
) => {
  try {
    const response = await api.post("/reset-password", {
      emailId,
      code,
      password,
    });
    return response.data;
  } catch (error: any) {
    if (error?.response?.data) {
      console.error("Reset Password error:", error.response.data);
    } else {
      console.error("Reset Password error:", error.message || "Unknown error");
    }
    throw error;
  }
};

export const forceChangePasswordWithBackend = async (
  emailId: string,
  newPassword: string,
  session: string
) => {
  try {
    const response = await api.post("/force-change-password", {
      emailId,
      newPassword,
      session,
    });
    return response.data;
  } catch (error: any) {
    if (error?.response?.data) {
      console.error("Force Change Password error:", error.response.data);
    } else {
      console.error(
        "Force Change Password error:",
        error.message || "Unknown error"
      );
    }
    throw error;
  }
};
export const mockLoginAPI = async (
  request: LoginRequestDTO
): Promise<LoginResponseDTO> => {
  const user = mockUsers.find(
    (u) => u.username === request.username && u.password === request.password
  );

  if (user) {
    return {
      success: true,
      message: "Login successful!",
      token: "mock-token",
      role: user.role,
    };
  } else {
    return {
      success: false,
      message: "Invalid username or password.",
    };
  }
};

export const mockPasswordUpdateAPI = async (
  request: PasswordUpdateRequestDTO
): Promise<PasswordUpdateResponseDTO> => {
  if (
    request.password === "password123" &&
    request.confirmPassword === "password123"
  ) {
    return {
      success: true,
      message: "Password updated successfully!",
    };
  } else {
    return {
      success: false,
      message: "Password and Confirm Password do not match.",
    };
  }
};

export const mockPasswordResetConfirmationAPI =
  async (): Promise<ConfirmationResponseDTO> => {
    return {
      success: true,
      message: "Password reset confirmed successfully!",
    };
  };

export const mockForgotPasswordAPI = async (
  request: ForgotPasswordRequestDTO
): Promise<ForgotPasswordResponseDTO> => {
  const validEmails = ["user1@test.com", "user2@test.com"];

  if (validEmails.includes(request.email)) {
    return {
      success: true,
      message: "A reset link has been sent to your email address.",
    };
  } else {
    return {
      success: false,
      message: "The provided email address is not registered.",
    };
  }
};

export const checkUser = async (props: ForgotPasswordRequestDTO) => {
  const endpointUrl = `${endpoints.checkUserExists}/${props.email}`;
  try {
    const response = await api.get(endpointUrl);
    console.log("User existence check response:", response.data.data);
    return response.data.data.exists;
  } catch (error) {
    console.error("CheckUser API Error:", error);
    throw error;
  }
};

export const getLoggedInUser = async () => {
  try {
    const organizationId = getApiOrganizationId();
    const response = await api.get(`/logged-in-user`, {
      headers: { orgId: organizationId },
    });
    return response.data;
  } catch (error: any) {
    if (error?.response?.data) {
      console.error("Get logged-in user error:", error.response.data);
    } else {
      console.error(
        "Get logged-in user error:",
        error.message || "Unknown error"
      );
    }
    throw error;
  }
};
